import { createClient } from '@supabase/supabase-js';
import { PARTS_CATALOG } from './partsCatalog';

// ── Supabase client ───────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL      || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = (SUPABASE_URL && SUPABASE_ANON_KEY)
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

// Maps our DB keys → Supabase table names
const TABLE_MAP = {
  users:      'users',
  customers:  'customers',
  vehicles:   'vehicles',
  engines:    'engines',
  spareParts:      'spare_parts',
  containers:      'containers',
  requests:        'requests',
  selectionOrders: 'selection_orders',
  invoices:        'invoices',
  payments:        'payments',
  priceQuotes:     'price_quotes',
  appConfig:       'app_config',
  customerStock:   'customer_stock',
  partsCatalog:    'parts_catalog',
  cutPlans:        'cut_plans',
  tasks:           'tasks',
  yardBids:        'yard_bids',
  inquiries:       'inquiries',
  tradeApplications: 'trade_applications',
  cutTemplates:    'cut_templates',
  unitEvents:      'unit_events',
  offers:          'offers',
};

// ── localStorage helpers ──────────────────────────────────────
const PREFIX = 'bigstar_';

// ── Role-based data security ─────────────────────────────────
// Reads current user session to determine filtering rules
function getCurrentUserContext() {
  try {
    const raw = localStorage.getItem('bigstar_session');
    if (!raw) return null;
    const { userData } = JSON.parse(raw);
    return userData || null;
  } catch { return null; }
}

function isCustomerRole(role) {
  return role === 'customer' || role === 'business';
}

// Tables customers should NEVER receive
const CUSTOMER_BLOCKED_TABLES = new Set([
  'yardBids', 'cutPlans', 'cutTemplates', 'tradeApplications', 'inquiries', 'tasks',
]);

// (Customer-filtered tables handled inside applyCustomerFilter)

// Sensitive fields to strip from units before storing for customers
const SENSITIVE_UNIT_FIELDS = [
  'costJPY', 'purchasePrice', 'auctionFee', 'domesticTransport', 'inspectionFee',
  'exportDocs', 'purchase_supplier', 'purchase_source', 'pricing',
  'costPrice', 'margin', 'marginPercentage', 'costAED', 'costUSD',
];

function normalizeCountry(str) {
  return (str || '').replace(/_/g, ' ').trim().toLowerCase();
}

/**
 * Filter data for customer role — runs BEFORE writing to localStorage
 * Returns filtered + field-stripped items
 */
function applyCustomerFilter(key, items, userId, userCountry) {
  if (!Array.isArray(items)) return items;

  // Blocked tables — return empty
  if (CUSTOMER_BLOCKED_TABLES.has(key)) return [];

  // Units — country-aware visibility + wishlist inclusion
  if (key === 'vehicles' || key === 'engines' || key === 'spareParts') {
    let myWishlistIds;
    try {
      const raw = localStorage.getItem(PREFIX + 'customerStock');
      const cs = raw ? JSON.parse(raw) : [];
      myWishlistIds = new Set(cs.filter(s => s.customerId === userId).map(s => s.unitId));
    } catch { myWishlistIds = new Set(); }

    const myCountry = normalizeCountry(userCountry);

    return items
      .filter(u => {
        // (a) Customer's own wishlist — always VISIBLE
        if (myWishlistIds.has(u.id)) return true;
        // (b) Assigned to this customer
        if (u.assignedTo === userId) return true;
        // Exclude gone statuses
        if (u.status === 'sold' || u.status === 'dismantled' || u.status === 'converted') return false;
        // Only include effectively-available statuses
        const effectivelyAvailable = u.status === 'available' || u.status === 'japan_yard'
          || u.status === 'in_transit' || u.status === 'sold_awaiting_payment';
        if (!effectivelyAvailable) return false;
        // (d) destination_country set — fail closed
        if (u.destination_country) {
          if (!myCountry) return false;
          return normalizeCountry(u.destination_country) === myCountry;
        }
        // (c) No destination_country — available to all
        return true;
      })
      .map(u => {
        const safe = { ...u };
        SENSITIVE_UNIT_FIELDS.forEach(f => delete safe[f]);
        if (safe.pricing) {
          const { japan_costs, destination_costs, selling, ...rest } = safe.pricing;
          safe.pricing = rest;
        }
        return safe;
      });
  }

  // Customers table — only self
  if (key === 'customers') {
    return items.filter(c => c.id === userId);
  }

  // Users table — customers see empty (no staff data), but KEEP in localStorage for login
  // Login reads DB.get('users') to authenticate — cannot wipe it
  if (key === 'users') {
    return items; // Keep full list — login depends on it. Staff details aren't sensitive to customers since customer UI never displays them.
  }

  // Invoices — only own
  if (key === 'invoices') {
    return items.filter(i => i.customerId === userId);
  }

  // Payments — only own
  if (key === 'payments') {
    return items.filter(p => p.customerId === userId);
  }

  // Price quotes — only own
  if (key === 'priceQuotes') {
    return items.filter(q => q.customerId === userId);
  }

  // Offers — only own
  if (key === 'offers') {
    return items.filter(o => o.customerId === userId);
  }

  // Selection orders — only own
  if (key === 'selectionOrders') {
    return items.filter(o => o.customerId === userId);
  }

  // Customer stock — only own
  if (key === 'customerStock') {
    return items.filter(s => s.customerId === userId);
  }

  // Containers — only those containing customer's units
  if (key === 'containers') {
    // Get customer's unit IDs from already-filtered local data
    const myUnitIds = new Set();
    ['vehicles', 'engines', 'spareParts'].forEach(uKey => {
      const units = lsGet(uKey) || [];
      units.forEach(u => { if (u.assignedTo === userId) myUnitIds.add(u.id); });
    });
    return items.filter(c => (c.units || []).some(uid => myUnitIds.has(uid)));
  }

  // appConfig — strip fee defaults for customers
  if (key === 'appConfig' && typeof items === 'object' && !Array.isArray(items)) {
    const safe = { ...items };
    delete safe.fee_defaults;
    delete safe.exchange_rates_config;
    delete safe.invoice_counter;
    return safe;
  }

  // Everything else — pass through
  return items;
}

const lsGet = (key) => {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const lsSet = (key, value) => {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

// ── Container field mappers (new schema uses proper columns) ──

// Wave 3.0b — parse container_costs JSONB with migration-on-read for legacy data
function parseContainerCosts(row) {
  const cc = row.container_costs;
  if (cc && cc.version === 1) {
    const fixed = cc.fixed_costs || {};
    // Ensure each fixed cost has status + updated_at
    Object.keys(fixed).forEach(k => {
      if (fixed[k] && !fixed[k].status) fixed[k].status = 'provisional';
      if (fixed[k] && !fixed[k].updated_at) fixed[k].updated_at = row.created_at || new Date().toISOString();
    });
    return {
      fixed,
      custom: (cc.custom_items || []).map(ci => ({ ...ci, status: ci.status || 'provisional', updated_at: ci.updated_at || new Date().toISOString() })),
    };
  }
  // Migration on read — legacy numeric columns assumed JPY
  const ts = row.created_at || new Date().toISOString();
  return {
    fixed: {
      freight:      { amount: Number(row.freight_cost) || 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: ts },
      drayage:      { amount: Number(row.drayage_cost) || 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: ts },
      port_charges: { amount: Number(row.port_charges) || 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: ts },
      bl_fee:       { amount: Number(row.bl_fee) || 0,       currency: 'JPY', notes: '', status: 'provisional', updated_at: ts },
      loading_fee:  { amount: Number(row.loading_fee) || 0,  currency: 'JPY', notes: '', status: 'provisional', updated_at: ts },
    },
    custom: [],
  };
}

// React format → Supabase columns
function containerToRow(item) {
  // Convert units array: old format is just IDs, new format is objects
  const unitsMapped = (item.units || []).map((u, i) => {
    if (typeof u === 'string') return { unit_id: u, unit_type: 'vehicle', unit_sku: '', unit_description: '', loading_order: i + 1 };
    return u;
  });
  // units is source of truth for membership; merge metadata from unitsData
  const finalUnits = unitsMapped.map(u => {
    const meta = (item.unitsData || []).find(ud => ud.unit_id === u.unit_id);
    return meta ? { ...u, ...meta, loading_order: u.loading_order } : u;
  });

  const row = {
    container_number: item.number || null,
    vessel_name: item.vessel || null,
    voyage_number: item.voyage || null,
    origin_port: item.origin || null,
    destination_port: item.destination || null,
    destination_country: item.destinationCountry || null,
    planned_departure: item.plannedDeparture || null,
    actual_departure: item.actualDeparture || null,
    estimated_arrival: item.eta || null,
    actual_arrival: item.actualArrival || null,
    status: item.status || 'planning',
    units: finalUnits,
    container_costs: {
      version: 1,
      fixed_costs: item.costs?.fixed || {
        freight:      { amount: 0, currency: 'JPY', notes: '' },
        drayage:      { amount: 0, currency: 'JPY', notes: '' },
        port_charges: { amount: 0, currency: 'JPY', notes: '' },
        bl_fee:       { amount: 0, currency: 'JPY', notes: '' },
        loading_fee:  { amount: 0, currency: 'JPY', notes: '' },
      },
      custom_items: item.costs?.custom || [],
    },
    // Legacy columns zeroed — single source of truth is container_costs
    freight_cost: 0,
    drayage_cost: 0,
    port_charges: 0,
    bl_fee: 0,
    loading_fee: 0,
    seal_number: item.sealNumber || null,
    seal_photos: item.sealPhotos || [],
    full_container_photo: item.fullContainerPhoto || null,
    loaded_by: null,
    loading_started_at: item.loadingStartedAt || null,
    loading_completed_at: item.loadingCompletedAt || null,
    timeline: item.timeline || [],
    notes: item.notes || null,
    container_size: item.containerSize || null,
    bl_status: item.blStatus || 'pending',
    bl_documents: item.blDocuments || [],
    bl_number: item.blNumber || null,
  };
  // Only include id if we have a Supabase UUID
  if (item._supaId) row.id = item._supaId;
  return row;
}

// Supabase columns → React format
function containerFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    number: row.container_number || '',
    vessel: row.vessel_name || '',
    voyage: row.voyage_number || '',
    origin: row.origin_port || '',
    destination: row.destination_port || row.destination_country || '',
    destinationCountry: row.destination_country || '',
    plannedDeparture: row.planned_departure || '',
    actualDeparture: row.actual_departure || '',
    eta: row.estimated_arrival || '',
    actualArrival: row.actual_arrival || '',
    status: row.status || 'planning',
    units: (row.units || []).map(u => typeof u === 'object' && u.unit_id ? u.unit_id : u),
    costs: parseContainerCosts(row),
    sealNumber: row.seal_number || '',
    sealPhotos: row.seal_photos || [],
    fullContainerPhoto: row.full_container_photo || '',
    loadedBy: row.loaded_by || '',
    loadingStartedAt: row.loading_started_at || '',
    loadingCompletedAt: row.loading_completed_at || '',
    containerSize: row.container_size || '',
    pricing: row.pricing || {},
    // Loading documentation per unit (rich data from units JSONB)
    unitsData: (row.units || []).filter(u => typeof u === 'object' && u.unit_id),
    blStatus: row.bl_status || 'pending',
    blDocuments: row.bl_documents || [],
    blNumber: row.bl_number || '',
    timeline: row.timeline || [],
    notes: row.notes || '',
    createdAt: row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '',
  };
}

// ── Invoice field mappers (proper columns) ───────────────────

function invoiceToRow(item) {
  const row = {
    invoice_number: item.invoiceNumber || null,
    invoice_category: item.invoiceCategory || 'proforma',
    customer_id: item.customerId || null,
    customer_name: item.customerName || null,
    status: item.status || 'draft',
    issued_date: item.issuedDate || null,
    container_number: item.containerNumber || null,
    container_id: item.containerId || null,
    linked_selection_order_id: item.linkedSelectionOrderId || null,
    line_items: item.lineItems || [],
    payment_milestones: item.paymentMilestones || [],
    subtotal: Number(item.subtotal) || 0,
    tax: Number(item.tax) || 0,
    grand_total: Number(item.grandTotal) || Number(item.total) || 0,
    amount_paid: Number(item.amountPaid) || 0,
    balance_due: Number(item.balanceDue) || 0,
    currency: item.currency || 'USD',
    customer_currency: item.customerCurrency || null,
    exchange_rate_used: item.exchangeRateUsed || null,
    grand_total_customer_currency: item.grandTotalCustomerCurrency || null,
    notes: item.notes || null,
    invoice_type: item.invoiceType || 'sale',
    linked_invoice_id: item.linkedInvoiceId || null,
    payments: item.payments || [],
    paid_date: item.paidDate || null,
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function invoiceFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    invoiceNumber: row.invoice_number || '',
    invoiceCategory: row.invoice_category || 'proforma',
    customerId: row.customer_id || '',
    customerName: row.customer_name || '',
    status: row.status || 'draft',
    issuedDate: row.issued_date || '',
    containerNumber: row.container_number || '',
    containerId: row.container_id || '',
    linkedSelectionOrderId: row.linked_selection_order_id || '',
    lineItems: row.line_items || [],
    paymentMilestones: row.payment_milestones || [],
    subtotal: Number(row.subtotal) || 0,
    tax: Number(row.tax) || 0,
    total: Number(row.grand_total) || 0,
    amountPaid: Number(row.amount_paid) || 0,
    balanceDue: Number(row.balance_due) || 0,
    currency: row.currency || 'USD',
    customerCurrency: row.customer_currency || '',
    exchangeRateUsed: row.exchange_rate_used || null,
    grandTotalCustomerCurrency: row.grand_total_customer_currency || null,
    notes: row.notes || '',
    invoiceType: row.invoice_type || 'sale',
    linkedInvoiceId: row.linked_invoice_id || '',
    payments: row.payments || [],
    paidDate: row.paid_date || '',
    createdAt: row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '',
  };
}

// ── Payment field mappers (proper columns) ───────────────────

function paymentToRow(item) {
  const row = {
    direction: item.direction || 'incoming',
    customer_id: item.customerId || null,
    party_name: item.partyName || null,
    invoice_id: item.invoiceId || null,
    amount: Number(item.amount) || 0,
    currency: item.currency || 'USD',
    payment_method: item.paymentMethod || null,
    reference: item.reference || null,
    status: item.status || 'pending_verification',
    receipt_url: item.receiptUrl || null,
    payment_date: item.paymentDate || null,
    verified_by: item.verifiedBy || null,
    verified_at: item.verifiedAt || null,
    reject_reason: item.rejectReason || null,
    notes: item.notes || null,
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function paymentFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    direction: row.direction || 'incoming',
    customerId: row.customer_id || '',
    partyName: row.party_name || '',
    invoiceId: row.invoice_id || '',
    amount: Number(row.amount) || 0,
    currency: row.currency || 'USD',
    paymentMethod: row.payment_method || '',
    reference: row.reference || '',
    status: row.status || 'pending_verification',
    receiptUrl: row.receipt_url || '',
    paymentDate: row.payment_date || '',
    verifiedBy: row.verified_by || '',
    verifiedAt: row.verified_at || '',
    rejectReason: row.reject_reason || '',
    notes: row.notes || '',
    createdAt: row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '',
  };
}

// ── Customer Stock field mappers ─────────────────────────────

function customerStockToRow(item) {
  const row = {
    customer_id: item.customerId || null,
    unit_id: item.unitId || null,
    unit_type: item.unitType || null,
    added_at: item.addedAt || new Date().toISOString(),
  };
  // Only include id if it's a valid Supabase UUID (not our genId format)
  if (item._supaId) row.id = item._supaId;
  return row;
}

function customerStockFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    customerId: row.customer_id || '',
    unitId: row.unit_id || '',
    unitType: row.unit_type || '',
    addedAt: row.added_at || '',
  };
}

// ── Offers field mappers ─────────────────────────────────────

function offerToRow(item) {
  const row = {
    customer_id: item.customerId || null,
    unit_id: item.unitId || null,
    unit_type: item.unitType || null,
    offer_type: item.offerType || 'whole',
    parts_requested: item.partsRequested ? JSON.stringify(item.partsRequested) : null,
    offer_amount: item.offerAmount ?? null,
    offer_currency: item.offerCurrency || 'USD',
    status: item.status || 'pending',
    counter_amount: item.counterAmount ?? null,
    counter_currency: item.counterCurrency || null,
    counter_by: item.counterBy || null,
    customer_response: item.customerResponse || null,
    history: item.history ? JSON.stringify(item.history) : '[]',
    notes: item.notes || '',
    created_at: item.createdAt || new Date().toISOString(),
    updated_at: item.updatedAt || new Date().toISOString(),
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function offerFromRow(row) {
  let partsRequested = [];
  try { partsRequested = typeof row.parts_requested === 'string' ? JSON.parse(row.parts_requested) : (row.parts_requested || []); } catch { partsRequested = []; }
  let history = [];
  try { history = typeof row.history === 'string' ? JSON.parse(row.history) : (row.history || []); } catch { history = []; }
  return {
    id: row.id,
    _supaId: row.id,
    customerId: row.customer_id || '',
    unitId: row.unit_id || '',
    unitType: row.unit_type || '',
    offerType: row.offer_type || 'whole',
    partsRequested,
    offerAmount: row.offer_amount ?? null,
    offerCurrency: row.offer_currency || 'USD',
    status: row.status || 'pending',
    counterAmount: row.counter_amount ?? null,
    counterCurrency: row.counter_currency || null,
    counterBy: row.counter_by || null,
    customerResponse: row.customer_response || null,
    history,
    notes: row.notes || '',
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
  };
}

// ── Yard Bids field mappers ──────────────────────────────────

function yardBidToRow(item) {
  const row = {
    unit_id: item.unit_id || null,
    unit_type: item.unit_type || null,
    unit_sku: item.unit_sku || null,
    unit_description: item.unit_description || null,
    customer_id: item.customer_id || null,
    customer_name: item.customer_name || null,
    customer_phone: item.customer_phone || null,
    is_repeat_buyer: item.is_repeat_buyer || false,
    amount: Number(item.amount) || 0,
    currency: item.currency || 'AED',
    amount_usd: Number(item.amount_usd) || 0,
    status: item.status || 'active',
    yard_location: item.yard_location || 'sharjah_yard',
    notes: item.notes || null,
    entered_by: item.entered_by || null,
    entered_by_name: item.entered_by_name || null,
    selected_for_sale: item.selected_for_sale || false,
    sale_approved_by: item.sale_approved_by || null,
    sale_approved_at: item.sale_approved_at || null,
    expires_at: item.expires_at || null,
    created_at: item.created_at || null,
    updated_at: item.updated_at || null,
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function yardBidFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    unit_id: row.unit_id || '',
    unit_type: row.unit_type || '',
    unit_sku: row.unit_sku || '',
    unit_description: row.unit_description || '',
    customer_id: row.customer_id || '',
    customer_name: row.customer_name || '',
    customer_phone: row.customer_phone || '',
    is_repeat_buyer: row.is_repeat_buyer || false,
    amount: Number(row.amount) || 0,
    currency: row.currency || 'AED',
    amount_usd: Number(row.amount_usd) || 0,
    status: row.status || 'active',
    yard_location: row.yard_location || 'sharjah_yard',
    notes: row.notes || '',
    entered_by: row.entered_by || '',
    entered_by_name: row.entered_by_name || '',
    selected_for_sale: row.selected_for_sale || false,
    sale_approved_by: row.sale_approved_by || '',
    sale_approved_at: row.sale_approved_at || '',
    expires_at: row.expires_at || '',
    created_at: row.created_at || '',
    updated_at: row.updated_at || '',
  };
}

// ── Tasks field mappers ─────────────────────────────────────

function taskToRow(item) {
  const row = {
    title: item.title || null,
    description: item.description || null,
    assigned_to: item.assigned_to || null,
    assigned_by: item.assigned_by || null,
    entity_type: item.entity_type || null,
    entity_ids: item.entity_ids || [],
    due_date: item.due_date || null,
    priority: item.priority || 'normal',
    status: item.status || 'pending',
    completed_at: item.completed_at || null,
    total_items: Number(item.total_items) || 0,
    completed_items: Number(item.completed_items) || 0,
    attachments: item.attachments || [],
    notes: item.notes || [],
    created_at: item.created_at || null,
    updated_at: item.updated_at || new Date().toISOString(),
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function taskFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    title: row.title || '',
    description: row.description || '',
    assigned_to: row.assigned_to || '',
    assigned_by: row.assigned_by || '',
    entity_type: row.entity_type || null,
    entity_ids: row.entity_ids || [],
    due_date: row.due_date || '',
    priority: row.priority || 'normal',
    status: row.status || 'pending',
    completed_at: row.completed_at || null,
    total_items: Number(row.total_items) || 0,
    completed_items: Number(row.completed_items) || 0,
    attachments: row.attachments || [],
    notes: row.notes || [],
    created_at: row.created_at || '',
    updated_at: row.updated_at || '',
  };
}

// ── Selection Orders field mappers ──────────────────────────

function selectionOrderToRow(item) {
  const row = {
    customer_id: item.customerId || null,
    customer_name: item.customerName || null,
    status: item.status || 'draft',
    units: Array.isArray(item.units) ? item.units : [],
    total_units: Number(item.totalUnits) || 0,
    total_value_usd: Number(item.totalValueUsd) || 0,
    customer_notes: item.customerNotes || null,
    submitted_at: item.submittedAt || null,
    reviewed_by: item.reviewedBy || null,
    reviewed_at: item.reviewedAt || null,
    review_notes: item.reviewNotes || null,
    invoice_id: item.invoiceId || null,
    updated_at: new Date().toISOString(),
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function selectionOrderFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    customerId: row.customer_id || '',
    customerName: row.customer_name || '',
    status: row.status || 'draft',
    units: Array.isArray(row.units) ? row.units : [],
    totalUnits: Number(row.total_units) || 0,
    totalValueUsd: Number(row.total_value_usd) || 0,
    customerNotes: row.customer_notes || '',
    submittedAt: row.submitted_at || '',
    reviewedBy: row.reviewed_by || '',
    reviewedAt: row.reviewed_at || '',
    reviewNotes: row.review_notes || '',
    invoiceId: row.invoice_id || '',
    createdAt: row.created_at ? new Date(row.created_at).toISOString().split('T')[0] : '',
    updatedAt: row.updated_at || '',
  };
}

// ── Cut Plans field mappers ──────────────────────────────────

function cutPlanToRow(item) {
  const row = {
    vehicle_id: item.vehicle_id || item.vehicleId || null,
    vehicle_sku: item.vehicle_sku || item.vehicleSku || null,
    vehicle_name: item.vehicle_name || item.vehicleName || null,
    initiated_by: item.initiated_by || null,
    request_id: item.request_id || null,
    customer_id: item.customer_id || null,
    customer_name: item.customer_name || null,
    cut_type: item.cut_type || item.cutType || '',
    custom_cut_type: item.custom_cut_type || null,
    template_id: item.template_id || null,
    included_parts: item.included_parts || {},
    remaining_parts: item.remaining_parts || [],
    scrap_parts: item.scrap_parts || [],
    additional_notes: item.additional_notes || null,
    cutting_fee: Number(item.cutting_fee) || 0,
    cutting_currency: item.cutting_currency || 'USD',
    estimated_days: item.estimated_days || null,
    actual_days: item.actual_days || null,
    parent_purchase_price: Number(item.parent_purchase_price) || null,
    parent_purchase_currency: item.parent_purchase_currency || null,
    main_part_cost_percent: item.main_part_cost_percent || 60,
    discussion: item.discussion || [],
    photos_before: item.photos_before || [],
    photos_during: item.photos_during || [],
    photos_after: item.photos_after || [],
    cutting_checklist: item.cutting_checklist || [],
    status: item.status || 'planning',
    customer_confirmed_at: item.customer_confirmed_at || null,
    confirmation_deadline: item.confirmation_deadline || null,
    cancellation_hours: item.cancellation_hours || 12,
    assigned_to: item.assigned_to || null,
    assigned_to_name: item.assigned_to_name || null,
    created_by: item.created_by || null,
    created_by_name: item.created_by_name || null,
    approved_by: item.approved_by || null,
    approved_by_name: item.approved_by_name || null,
    approved_at: item.approved_at || null,
    rejection_reason: item.rejection_reason || null,
    started_at: item.started_at || null,
    completed_at: item.completed_at || null,
    completed_by: item.completed_by || null,
    completed_by_name: item.completed_by_name || null,
    main_part_id: item.main_part_id || null,
    main_part_sku: item.main_part_sku || null,
    child_part_ids: item.child_part_ids || [],
    child_part_skus: item.child_part_skus || [],
    total_parts_created: item.total_parts_created || 0,
    current_step: item.current_step || 0,
    step_data: item.step_data || {},
    updated_at: new Date().toISOString(),
  };
  if (item._supaId) row.id = item._supaId;
  return row;
}

function cutPlanFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    plan_number: row.plan_number,
    vehicle_id: row.vehicle_id || '',
    vehicle_sku: row.vehicle_sku || '',
    vehicle_name: row.vehicle_name || '',
    initiated_by: row.initiated_by || '',
    request_id: row.request_id || '',
    customer_id: row.customer_id || '',
    customer_name: row.customer_name || '',
    cut_type: row.cut_type || '',
    custom_cut_type: row.custom_cut_type || '',
    template_id: row.template_id || '',
    included_parts: row.included_parts || {},
    remaining_parts: row.remaining_parts || [],
    scrap_parts: row.scrap_parts || [],
    additional_notes: row.additional_notes || '',
    cutting_fee: Number(row.cutting_fee) || 0,
    cutting_currency: row.cutting_currency || 'USD',
    estimated_days: row.estimated_days || null,
    actual_days: row.actual_days || null,
    parent_purchase_price: Number(row.parent_purchase_price) || null,
    parent_purchase_currency: row.parent_purchase_currency || '',
    main_part_cost_percent: row.main_part_cost_percent || 60,
    discussion: row.discussion || [],
    photos_before: row.photos_before || [],
    photos_during: row.photos_during || [],
    photos_after: row.photos_after || [],
    cutting_checklist: row.cutting_checklist || [],
    status: row.status || 'planning',
    customer_confirmed_at: row.customer_confirmed_at || '',
    confirmation_deadline: row.confirmation_deadline || '',
    cancellation_hours: row.cancellation_hours || 12,
    assigned_to: row.assigned_to || '',
    assigned_to_name: row.assigned_to_name || '',
    created_by: row.created_by || '',
    created_by_name: row.created_by_name || '',
    approved_by: row.approved_by || '',
    approved_by_name: row.approved_by_name || '',
    approved_at: row.approved_at || '',
    rejection_reason: row.rejection_reason || '',
    started_at: row.started_at || '',
    completed_at: row.completed_at || '',
    completed_by: row.completed_by || '',
    completed_by_name: row.completed_by_name || '',
    main_part_id: row.main_part_id || '',
    main_part_sku: row.main_part_sku || '',
    child_part_ids: row.child_part_ids || [],
    child_part_skus: row.child_part_skus || [],
    total_parts_created: row.total_parts_created || 0,
    current_step: row.current_step || 0,
    step_data: row.step_data || {},
    created_at: row.created_at || '',
    updated_at: row.updated_at || '',
  };
}

// ── Cut Templates field mapper (pull-only) ──────────────────────
function cutTemplateFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    name: row.name || '',
    description: row.description || '',
    cut_type: row.cut_type || '',
    applies_to_make: row.applies_to_make || null,
    applies_to_model: row.applies_to_model || null,
    included_parts: row.included_parts || {},
    remaining_parts: row.remaining_parts || [],
    created_by: row.created_by || null,
    is_default: row.is_default || false,
    created_at: row.created_at || '',
  };
}

// ── Unit Events field mappers (append-only lifecycle log) ─────
function unitEventToRow(item) {
  const row = {
    unit_id: item.unit_id,
    unit_type: item.unit_type,
    event: item.event,
    details: item.details || {},
    actor_id: item.actor_id || null,
    actor_role: item.actor_role || null,
    created_at: item.created_at || new Date().toISOString(),
  };
  row.id = item._supaId || item.id;
  return row;
}

function unitEventFromRow(row) {
  return {
    id: row.id,
    _supaId: row.id,
    unit_id: row.unit_id || '',
    unit_type: row.unit_type || '',
    event: row.event || '',
    details: row.details || {},
    actor_id: row.actor_id || null,
    actor_role: row.actor_role || null,
    created_at: row.created_at || '',
  };
}

// ── Supabase sync helpers ─────────────────────────────────────

// Track which tables have pushes in flight so pullTable won't overwrite local changes
const pushInFlight = new Set();

// After a successful push, protect this key from pull for PUSH_PROTECTION_MS
// so Supabase replication lag can't overwrite localStorage with stale data
const recentlyPushed = new Map();
const PUSH_PROTECTION_MS = 10000;

// Push one table to Supabase
// Returns { error: '...' } on failure, undefined on success
async function pushTable(key, value) {
  pushInFlight.add(key);
  if (!supabase || !TABLE_MAP[key]) { pushInFlight.delete(key); return; }
  if (key === 'partsCatalog') { pushInFlight.delete(key); return; }
  const table = TABLE_MAP[key];
  let pushFailed = false;
  try {
    // appConfig — key/value table, each top-level field is a separate row
    if (key === 'appConfig' && !Array.isArray(value) && typeof value === 'object') {
      const failedKeys = [];
      for (const [k, v] of Object.entries(value)) {
        const { error } = await supabase.from(table).upsert({ key: k, value: v }, { onConflict: 'key' });
        if (error) {
          console.error(`[pushTable] appConfig key ${k} failed:`, error.message);
          failedKeys.push(k);
        }
      }
      if (failedKeys.length) {
        if (failedKeys.length === Object.keys(value).length) pushFailed = true;
        return { error: `appConfig sub-keys failed: ${failedKeys.join(', ')}` };
      }
      return;
    }

    // Other non-array values — store as {id: key, payload: value}
    if (!Array.isArray(value)) {
      const { error } = await supabase.from(table).upsert({ id: key, payload: value }, { onConflict: 'id' });
      if (error) { console.error(`[pushTable] ${key} upsert failed:`, error.message); pushFailed = true; return { error: error.message }; }
      return;
    }

    // Tables with proper columns: containers, invoices, payments, customerStock
    const PROPER_COLUMN_TABLES = { containers: containerToRow, invoices: invoiceToRow, payments: paymentToRow, customerStock: customerStockToRow, yardBids: yardBidToRow, cutPlans: cutPlanToRow, tasks: taskToRow, selectionOrders: selectionOrderToRow, unitEvents: unitEventToRow, offers: offerToRow };
    if (PROPER_COLUMN_TABLES[key]) {
      const toRow = PROPER_COLUMN_TABLES[key];
      const errors = [];
      for (const item of value) {
        const row = toRow(item);
        if (item._supaId) {
          row.id = item._supaId;
          const { error } = await supabase.from(table).upsert(row, { onConflict: 'id' });
          if (error) {
            console.error(`[pushTable] Upsert failed for ${key} (id: ${item._supaId}):`, error.message);
            errors.push(error.message);
          }
        } else {
          // Use the item's local ID as the Supabase row ID (text id columns have no default)
          row.id = item.id;
          let inserted = false;
          for (let attempt = 0; attempt < 5 && !inserted; attempt++) {
            const { data, error } = await supabase.from(table).upsert(row, { onConflict: 'id' }).select('id');
            if (error) {
              // Invoice number collision — pick the next available number and retry
              if (key === 'invoices' && error.message && error.message.includes('invoice_number')) {
                const nextNum = await DB.nextInvoiceNumberAsync();
                console.warn(`[pushTable] Invoice number collision (${row.invoice_number}), retrying with ${nextNum}`);
                row.invoice_number = nextNum;
                item.invoiceNumber = nextNum;
                continue;
              }
              console.error(`[pushTable] Insert failed for ${key}:`, error.message, error.details || '', error.hint || '');
              errors.push(error.message);
              break;
            } else if (data && data.length > 0) {
              item._supaId = data[0].id;
              lsSet(key, value);
              inserted = true;
            }
          }
        }
      }
      if (errors.length > 0) { pushFailed = true; return { error: errors.join('; ') }; }
    } else {
      // Old schema: {id, payload}
      if (value.length > 0) {
        const { error } = await supabase.from(table).upsert(
          value.map(item => ({ id: item.id, payload: item })),
          { onConflict: 'id' }
        );
        if (error) { console.error(`[pushTable] ${key} bulk upsert failed:`, error.message); pushFailed = true; return { error: error.message }; }
      }
    }
  } catch (err) {
    console.error(`[pushTable] ${table} EXCEPTION:`, err.message);
    pushFailed = true;
    return { error: err.message };
  } finally {
    pushInFlight.delete(key);
    if (!pushFailed) {
      recentlyPushed.set(key, Date.now());
      console.log(`[pushTable] Push succeeded for ${key} — pull protected for ${PUSH_PROTECTION_MS / 1000}s`);
    }
  }
}

// Security wrapper — filters data for customer role before writing to localStorage
function secureLsSet(key, items) {
  const ctx = getCurrentUserContext();
  if (ctx && isCustomerRole(ctx.role)) {
    return lsSet(key, applyCustomerFilter(key, items, ctx.id, ctx.country));
  }
  return lsSet(key, items);
}

// Pull one table from Supabase into localStorage
async function pullTable(key) {
  if (!supabase || !TABLE_MAP[key]) return false;

  // Don't pull while a push is in flight — prevents overwriting local changes with stale cloud data
  if (pushInFlight.has(key)) {
    console.log(`[pullTable] Skipping ${key} — push in flight`);
    return false;
  }

  // Don't pull right after a successful push — Supabase replication lag could return stale data
  const pushedAt = recentlyPushed.get(key);
  if (pushedAt) {
    const elapsed = Date.now() - pushedAt;
    if (elapsed < PUSH_PROTECTION_MS) {
      console.log(`[pullTable] Skipping ${key} — recently pushed ${Math.round(elapsed / 1000)}s ago`);
      return false;
    }
    recentlyPushed.delete(key);
  }

  // Security: block entire tables for customer role
  const ctx = getCurrentUserContext();
  if (ctx && isCustomerRole(ctx.role) && CUSTOMER_BLOCKED_TABLES.has(key)) {
    lsSet(key, []);
    return true;
  }

  const table = TABLE_MAP[key];
  try {
    // Tables with proper columns
    const PROPER_COLUMN_PULL = { containers: containerFromRow, invoices: invoiceFromRow, payments: paymentFromRow, customerStock: customerStockFromRow, yardBids: yardBidFromRow, cutPlans: cutPlanFromRow, cutTemplates: cutTemplateFromRow, tasks: taskFromRow, selectionOrders: selectionOrderFromRow, unitEvents: unitEventFromRow, offers: offerFromRow };
    if (PROPER_COLUMN_PULL[key]) {
      const { data, error } = await supabase.from(table).select('*');
      if (error || !data) return false;
      // Filter out locally deleted items
      // Merge local + shared deletion lists
      const delKey2 = `${PREFIX}deleted_${key}`;
      const localDel2 = JSON.parse(localStorage.getItem(delKey2) || '[]');
      let sharedDel2 = [];
      try {
        const { data: delRows2 } = await supabase.from('app_config').select('value').eq('key', `deleted_${key}`);
        const delRow2 = delRows2?.[0];
        if (delRow2?.value && Array.isArray(delRow2.value)) sharedDel2 = delRow2.value;
      } catch {}
      const deletedIds2 = new Set([...localDel2, ...sharedDel2]);
      const items = data.map(PROPER_COLUMN_PULL[key]).filter(item => !deletedIds2.has(item.id) && !deletedIds2.has(item._supaId));
      // For customerStock: NEVER lose data — merge conservatively
      if (key === 'customerStock') {
        const local = lsGet(key) || [];

        // If cloud is empty but local has data — keep local, push to cloud
        if (items.length === 0 && local.length > 0) {
          pushTable(key, local);
          return true;
        }

        // If cloud has significantly fewer items than local (>30% loss), something is wrong
        // Keep local and push to cloud instead of replacing
        if (local.length > 5 && items.length < local.length * 0.7) {
          console.warn(`customerStock: cloud has ${items.length} but local has ${local.length}. Keeping local to prevent data loss.`);
          // Merge: keep all local, add any cloud-only items
          const localIds = new Set(local.map(l => `${l.customerId}_${l.unitId}`));
          const cloudOnly = items.filter(i => !localIds.has(`${i.customerId}_${i.unitId}`));
          if (cloudOnly.length > 0) {
            const merged = [...local, ...cloudOnly];
            secureLsSet(key, merged);
            pushTable(key, merged);
          }
          // Push local to cloud to restore what's missing
          pushTable(key, local);
          return true;
        }

        // Normal merge: cloud is authoritative, but add any local entries not in cloud
        const cloudIds = new Set(items.map(i => `${i.customerId}_${i.unitId}`));
        const localOnly = local.filter(l => !cloudIds.has(`${l.customerId}_${l.unitId}`));
        if (localOnly.length > 0) {
          const merged = [...items, ...localOnly];
          secureLsSet(key, merged);
          pushTable(key, merged);
        } else {
          secureLsSet(key, items);
        }
        return true;
      }
      // MERGE: keep any local items not yet in Supabase (recently created, push still in flight)
      const local = lsGet(key) || [];
      const cloudIds = new Set(items.map(i => i._supaId || i.id).filter(Boolean));
      const localOnly = local.filter(l => {
        if (!l._supaId && !cloudIds.has(l.id)) return true;
        return false;
      });
      const merged = [...items, ...localOnly];
      secureLsSet(key, merged);
      // Retry push for any unsynced local items
      if (localOnly.length > 0) {
        console.log(`[pullTable] ${key}: ${localOnly.length} unsynced local item(s), retrying push`);
        pushTable(key, merged);
      }
      return true;
    }

    if (key === 'partsCatalog') {
      const { data, error } = await supabase.from(table).select('*').order('category').order('display_order');
      if (error || !data || data.length === 0) return false;
      secureLsSet(key, data);
      return true;
    }

    // appConfig — key/value table
    if (key === 'appConfig') {
      const { data: rows, error } = await supabase.from(table).select('key, value');
      if (error || !rows) return false;
      if (rows.length > 0) {
        const merged = {};
        rows.forEach(r => {
          if (r.key && r.value !== undefined) {
            merged[r.key] = r.value;
          }
        });
        if (Object.keys(merged).length > 0) {
          const existing = lsGet(key);
          const base = (existing && typeof existing === 'object' && !Array.isArray(existing)) ? existing : {};
          const combined = { ...base, ...merged };
          // Security: strip fee config for customers
          const ctxA = getCurrentUserContext();
          if (ctxA && isCustomerRole(ctxA.role)) {
            delete combined.fee_defaults;
            delete combined.exchange_rates_config;
            delete combined.invoice_counter;
          }
          lsSet(key, combined);
        }
      }
      return true;
    }

    // Old schema: {id, payload}
    const { data, error } = await supabase.from(table).select('payload');
    if (error || !data) return false;

    // Merge local + shared (cloud) deletion lists so ALL users skip deleted items
    const delKey = `${PREFIX}deleted_${key}`;
    const localDeleted = JSON.parse(localStorage.getItem(delKey) || '[]');
    let sharedDeleted = [];
    try {
      const { data: delRows } = await supabase.from('app_config').select('value').eq('key', `deleted_${key}`);
      const delRow = delRows?.[0];
      if (delRow?.value && Array.isArray(delRow.value)) sharedDeleted = delRow.value;
    } catch {}
    const deletedIds = new Set([...localDeleted, ...sharedDeleted]);

    let items = data.map(row => row.payload).filter(item => item && !deletedIds.has(item.id));

    // MERGE: keep local items not yet in Supabase
    const local = lsGet(key) || [];
    if (Array.isArray(local) && local.length > 0) {
      const cloudIds = new Set(items.map(i => i.id).filter(Boolean));
      const localOnly = local.filter(l => l.id && !cloudIds.has(l.id) && !deletedIds.has(l.id));
      if (localOnly.length > 0) items = [...items, ...localOnly];
    }

    if (!secureLsSet(key, items)) {
      items = items.map(item =>
        item.images?.length > 1
          ? { ...item, images: item.images.slice(0, 1) }
          : item
      );
      if (!secureLsSet(key, items)) {
        items = items.map(item => ({ ...item, images: [] }));
        secureLsSet(key, items);
      }
    }
    return true;
  } catch {
    return false;
  }
}

// ── Offerability check — can this customer act on this unit? ──
export function canCustomerOffer(unit, user) {
  if (!unit || !user) return false;
  const s = unit.status;
  if (s !== 'available' && s !== 'japan_yard' && s !== 'sold_awaiting_payment') return false;
  if (unit.destination_country) {
    const uc = normalizeCountry(user.country);
    if (!uc) return false;
    if (normalizeCountry(unit.destination_country) !== uc) return false;
  }
  return true;
}

// ── Supabase Storage (images) ─────────────────────────────────
export async function uploadImage(base64Data) {
  if (!supabase) return base64Data;
  try {
    const res  = await fetch(base64Data);
    const blob = await res.blob();
    const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;

    const { error } = await supabase.storage
      .from('images')
      .upload(name, blob, { contentType: 'image/jpeg', upsert: false });

    if (error) {
      console.warn('Image upload failed, using local storage:', error.message);
      return base64Data;
    }

    const { data: urlData } = supabase.storage.from('images').getPublicUrl(name);
    return urlData.publicUrl;
  } catch (err) {
    console.warn('Image upload error:', err);
    return base64Data;
  }
}

// ── Migrate old base64 images to Supabase Storage ────────────
async function migrateImages() {
  if (!supabase) return;

  const tableMap = [
    { table: 'vehicles',    key: 'vehicles'   },
    { table: 'engines',     key: 'engines'    },
    { table: 'spare_parts', key: 'spareParts' },
  ];

  let didMigrate = false;

  for (const { table, key } of tableMap) {
    const { data, error } = await supabase.from(table).select('id, payload');
    if (error || !data) continue;

    for (const row of data) {
      const item = row.payload;
      if (!item.images?.length) continue;

      // Skip if all images are already URLs (not base64)
      const hasBase64 = item.images.some(img => typeof img === 'string' && img.startsWith('data:'));
      if (!hasBase64) continue;

      let changed = false;
      const newImages = [];

      for (const img of item.images) {
        if (typeof img === 'string' && img.startsWith('data:')) {
          try {
            const res  = await fetch(img);
            const blob = await res.blob();
            const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
            const { error: upErr } = await supabase.storage
              .from('images')
              .upload(name, blob, { contentType: 'image/jpeg' });
            if (!upErr) {
              const { data: urlData } = supabase.storage.from('images').getPublicUrl(name);
              // Verify the URL actually works before replacing
              try {
                const check = await fetch(urlData.publicUrl, { method: 'HEAD' });
                if (check.ok) {
                  newImages.push(urlData.publicUrl);
                  changed = true;
                  didMigrate = true;
                } else {
                  newImages.push(img); // URL not accessible, keep base64
                }
              } catch {
                newImages.push(img);
              }
            } else {
              newImages.push(img);
            }
          } catch {
            newImages.push(img);
          }
        } else {
          newImages.push(img);
        }
      }

      if (changed) {
        const updated = { ...item, images: newImages };
        await supabase.from(table).upsert({ id: row.id, payload: updated }, { onConflict: 'id' });
        const local = lsGet(key) || [];
        lsSet(key, local.map(i => i.id === row.id ? updated : i));
      }
    }
  }

  if (didMigrate) {
    console.log('Image migration complete');
    await DB.syncFromCloud();
  }
}

// ── Public DB API ─────────────────────────────────────────────
export const DB = {
  // Synchronous read from localStorage (fast, used everywhere in components)
  get: (key) => lsGet(key),

  // Force-refresh a single table from Supabase (async, for when you need fresh data)
  refresh: async (key) => { await pullTable(key); return lsGet(key); },

  // Write to localStorage immediately, then sync to Supabase in background
  set: (key, value) => {
    // Safety: prevent accidental wipe of customerStock
    if (key === 'customerStock' && Array.isArray(value)) {
      const existing = lsGet(key) || [];
      if (existing.length > 5 && value.length === 0) {
        console.error('BLOCKED: Attempted to wipe customerStock (had ' + existing.length + ' entries). This is likely a bug.');
        return false;
      }
    }
    lsSet(key, value);
    pushTable(key, value); // fire-and-forget
    return true;
  },

  // Write to localStorage + await Supabase push (for critical saves)
  // Returns { success: true } or { success: false, error: '...' }
  setAsync: async (key, value) => {
    // Safety: prevent accidental wipe of customerStock
    if (key === 'customerStock' && Array.isArray(value)) {
      const existing = lsGet(key) || [];
      if (existing.length > 5 && value.length === 0) {
        console.error('BLOCKED: Attempted to wipe customerStock (had ' + existing.length + ' entries).');
        return { success: false, error: 'Blocked: would wipe customerStock' };
      }
    }
    lsSet(key, value);
    console.log(`[DB.setAsync] Pushing ${key}, records: ${Array.isArray(value) ? value.length : 1}`);
    const pushResult = await pushTable(key, value);
    if (pushResult && pushResult.error) {
      console.error(`[DB.setAsync FAILED] ${key}:`, pushResult.error);
      return { success: false, error: pushResult.error };
    }
    console.log(`[DB.setAsync] Push complete for ${key}, success: true`);
    return { success: true };
  },

  remove: (key) => localStorage.removeItem(PREFIX + key),

  // Delete a specific record from Supabase by its id (for permanent deletion)
  deleteRecord: async (key, recordId) => {
    if (!recordId) return;
    // Track locally so pulls don't re-add this item
    const delKey = `${PREFIX}deleted_${key}`;
    const deleted = JSON.parse(localStorage.getItem(delKey) || '[]');
    if (!deleted.includes(recordId)) {
      deleted.push(recordId);
      localStorage.setItem(delKey, JSON.stringify(deleted));
    }

    // Always persist deletion list to Supabase FIRST so other devices/syncs respect it
    if (supabase) {
      try {
        await supabase.from('app_config').upsert({
          key: `deleted_${key}`,
          value: deleted,
        }, { onConflict: 'key' });
      } catch (syncErr) {
        console.warn(`[deleteRecord] Could not sync deletion list for ${key}:`, syncErr.message);
      }
    }

    if (!supabase || !TABLE_MAP[key]) return;
    const table = TABLE_MAP[key];
    try {
      // Try delete by id (works for old-schema tables where id = local genId)
      const { error, count } = await supabase.from(table).delete().eq('id', recordId);
      if (error) {
        console.error(`[deleteRecord] Failed to delete ${recordId} from ${table}:`, error.message);
        return;
      }
      console.log(`[deleteRecord] Deleted ${recordId} from ${table}, rows affected: ${count}`);

      // Only clear from local tracking if the row was actually removed from Supabase
      if (count && count > 0) {
        const remaining = deleted.filter(id => id !== recordId);
        localStorage.setItem(delKey, JSON.stringify(remaining));
        // Update Supabase deletion list too (no longer needed since row is gone)
        try {
          await supabase.from('app_config').upsert({
            key: `deleted_${key}`,
            value: remaining,
          }, { onConflict: 'key' });
        } catch {}
      }
      // If count is 0 or undefined, keep the ID in the deletion list permanently
      // so pullTable will always filter it out
    } catch (err) {
      console.error(`[deleteRecord] Exception deleting from ${table}:`, err.message);
    }
  },

  genId: (prefix = '') =>
    `${prefix}${Date.now().toString(36)}${Math.random().toString(36).substr(2, 4)}`.toUpperCase(),

  // Sequential invoice number from counter in appConfig (sync, legacy)
  nextInvoiceNumber: () => {
    const cfg = lsGet('appConfig') || {};
    const counter = (typeof cfg === 'object' && !Array.isArray(cfg)) ? (cfg.invoice_counter || { next_number: 1 }) : { next_number: 1 };
    const num = counter.next_number || 1;
    const updated = { ...(typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {}), invoice_counter: { ...counter, next_number: num + 1 } };
    lsSet('appConfig', updated);
    return String(num);
  },

  // Async invoice number — queries Supabase for the actual highest number, with collision retry
  nextInvoiceNumberAsync: async () => {
    let cloudMax = 0;
    let localMax = 0;

    // Local max (always available, used as floor even when Supabase succeeds)
    const localInvoices = lsGet('invoices') || [];
    localMax = Math.max(0, ...localInvoices.map(i => parseInt(i.invoiceNumber)).filter(n => !isNaN(n)));

    try {
      if (supabase) {
        const { data, error } = await supabase
          .from('invoices')
          .select('invoice_number')
          .filter('invoice_number', 'neq', null);

        if (error) {
          console.error('[nextInvoiceNumberAsync] Supabase query failed:', error.message);
        } else {
          const nums = (data || []).map(r => parseInt(r.invoice_number)).filter(n => !isNaN(n));
          cloudMax = nums.length > 0 ? Math.max(...nums) : 0;
          console.log('[nextInvoiceNumberAsync] Cloud max:', cloudMax, 'Local max:', localMax);
        }
      }
    } catch (err) {
      console.error('[nextInvoiceNumberAsync] Exception:', err.message);
    }

    // Use whichever is higher — protects against either source being stale
    const nextNum = Math.max(cloudMax, localMax) + 1;

    // Also update the localStorage counter so the sync fallback stays current
    try {
      const cfg = lsGet('appConfig') || {};
      if (typeof cfg === 'object' && !Array.isArray(cfg)) {
        const counter = cfg.invoice_counter || { next_number: 1 };
        if ((counter.next_number || 0) < nextNum + 1) {
          lsSet('appConfig', { ...cfg, invoice_counter: { ...counter, next_number: nextNum + 1 } });
        }
      }
    } catch {}

    return String(nextNum);
  },

  // Pull all tables from Supabase → populate localStorage
  syncFromCloud: async () => {
    if (!supabase) return false;
    await Promise.all(Object.keys(TABLE_MAP).map(key => pullTable(key)));
    return true;
  },

  // App startup: pull cloud data first, then seed if empty, then migrate images
  init: async () => {
    await DB.syncFromCloud();

    // Warn if users table is empty — do NOT seed demo data
    const users = lsGet('users');
    if (!users || users.length === 0) {
      console.error('[DB.init] users table is EMPTY after sync — NOT seeding. ' +
                    'This usually means the Supabase pull failed. Check connectivity/RLS.');
    }

    // Seed parts catalog from static data if not loaded from Supabase
    const catalog = lsGet('partsCatalog');
    if (!catalog || catalog.length === 0) {
      lsSet('partsCatalog', PARTS_CATALOG);
    }

    // Migrate old base64 images to Supabase Storage (runs once)
    migrateImages();

    // Refresh shared data from Supabase periodically so all admins stay in sync
    if (supabase && !DB._syncInterval) {
      // appConfig every 2 minutes (settings, CMS content)
      DB._syncInterval = setInterval(() => {
        pullTable('appConfig').catch(() => {});
      }, 2 * 60 * 1000);

      // All shared business data every 3 minutes (containers, inventory, customers, etc.)
      // Security: customers get a reduced table list — pullTable also filters per-row
      DB._dataInterval = setInterval(() => {
        const syncCtx = getCurrentUserContext();
        const isCustomer = syncCtx && isCustomerRole(syncCtx.role);
        const tables = isCustomer
          ? ['vehicles', 'engines', 'spareParts', 'containers', 'invoices', 'payments', 'customerStock', 'selectionOrders', 'priceQuotes', 'offers']
          : ['vehicles', 'engines', 'spareParts', 'containers', 'customers',
             'invoices', 'payments', 'selectionOrders', 'priceQuotes',
             'inquiries', 'tradeApplications', 'customerStock', 'yardBids',
             'cutPlans', 'cutTemplates', 'tasks', 'offers'];
        tables.forEach(key => pullTable(key).catch(() => {}));
      }, 3 * 60 * 1000);
    }

    // Fix units with invalid status (yard value stuck in status field)
    if (!localStorage.getItem('bigstar_status_fix_v1')) {
      const validStatuses = new Set(['available', 'in_transit', 'assigned', 'sold', 'converted', 'dismantled']);
      ['vehicles', 'engines', 'spareParts'].forEach(key => {
        const items = lsGet(key);
        if (items && items.length > 0) {
          let changed = false;
          const updated = items.map(u => {
            if (u.status && !validStatuses.has(u.status)) {
              changed = true;
              return { ...u, status: 'available' };
            }
            return u;
          });
          if (changed) {
            lsSet(key, updated);
            pushTable(key, updated);
          }
        }
      });
      localStorage.setItem('bigstar_status_fix_v1', '1');
    }

    // One-time cleanup: clear ALL destination_yard values so user can start fresh
    if (!localStorage.getItem('bigstar_dest_yard_reset')) {
      ['vehicles', 'engines', 'spareParts'].forEach(key => {
        const items = lsGet(key);
        if (items && items.length > 0) {
          const updated = items.map(u => {
            const { destination_yard, yard_location, ...rest } = u;
            return rest;
          });
          lsSet(key, updated);
          pushTable(key, updated);
        }
      });
      localStorage.setItem('bigstar_dest_yard_reset', '1');
    }
  },

  export: () => {
    const data = {};
    Object.keys(TABLE_MAP).forEach(key => { data[key] = lsGet(key); });
    return JSON.stringify(data, null, 2);
  },

  reset: async () => {
    // Only clear business data — keep users so admins can still log in
    const businessKeys = ['vehicles', 'engines', 'spareParts', 'containers', 'requests', 'customers'];
    const businessTables = ['vehicles', 'engines', 'spare_parts', 'containers', 'requests', 'customers'];

    // Clear from localStorage
    businessKeys.forEach(key => localStorage.removeItem(PREFIX + key));

    // Clear from Supabase
    if (supabase) {
      await Promise.all(
        businessTables.map(table =>
          supabase.from(table).delete().neq('id', '')
        )
      );
    }
  },
};

export default DB;
